/* eslint-disable max-lines */
import { IconButton, UIText3 } from '@cian/ui-kit';
import { IconFunctionalWarningCircle16 } from '@cian/ui-kit-design-tokens/icons';
import * as React from 'react';
import { useSelector } from 'react-redux';

import {
  useMessageHtml,
  useMessageText,
  useImagesNode,
  useImageAsDocument,
  useDocument,
  useUserName,
  useOutcomingMessageStyle,
} from './hooks';
import { trackClickMessage, trackHiddenMessagePhoneClick, trackShowMessage } from './tracking';
import { getStatusIcon } from './utils';
import { ChatMessageChatbotGreeting, ImageNode } from '../../components/ChatMessageChatbotGreeting';
import { ChatMessageLayout } from '../../components/ChatMessageLayout';
import { MessageErrorMenu } from '../../components/MessageErrorMenu';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { useContextMenu } from '../../hooks/useContextMenu';
import { useLongPress } from '../../hooks/useLongPress';
import { currentOpenChatIDSelector } from '../../selectors';
import { ERequestApiStatus } from '../../types/appState';
import { TMessage, TOutcomingStatus } from '../../types/messages';
import { useApplicationContext } from '../../utils/applicationContext';
import { shouldShowDailyrentButtons } from '../../utils/dailyrent/shouldShowDailyrentButtons';
import { ActionButtonContainer } from '../ActionButtonContainer';
import { BubbleMessageContainer } from '../BubbleMessageContainer';
import { useChatMessageMenu } from '../ChatMessageMenuContainer';
import { DocumentMessageContainer } from '../DocumentMessageContainer';
import { ImageAsDocumentMessageContainer } from '../ImageAsDocumentMessageContainer';
import { InfoMessageContainer } from '../InfoMessageContainer';
import { OfferGalleryContainer } from '../OfferGalleryContainer/OfferGalleryContainer';
import { OfferMessageContainer } from '../OfferMessageContainer';

type TChatMessageContainerProps = {
  message: TMessage & { status: TOutcomingStatus };
  messageId: string;
  isLastInChain: boolean;
  isFirstUserPhrase: boolean;
  isOfferGallery: boolean;
};

export const ChatMessageContainer: React.FC<TChatMessageContainerProps> = ({
  message,
  messageId,
  isLastInChain,
  isFirstUserPhrase,
  isOfferGallery,
}) => {
  const [showErrorMenu, setShowErrorMenu] = React.useState(false);
  const chatId = useSelector(currentOpenChatIDSelector) || '';
  const {
    message: text,
    htmlText,
    created,
    status,
    files,
    actionButtons,
    actionButtonsV3,
    isRead,
    assumptions,
    bubbles,
    userId,
    isOutcoming,
    communicationId,
  } = message;
  const isOfferMessage = React.useMemo(() => Boolean(message?.offers?.length), [message]);
  const isChatbotOfferMessage = chatId === CHATBOT_ID && isOfferMessage && !isOutcoming;
  const offer = message?.offers && message?.offers[0];

  const buttonRef = React.createRef<HTMLButtonElement>();
  const messageRef = React.useRef<HTMLDivElement | null>(null);

  const { showChatMessageMenu, hideChatMessageMenu } = useChatMessageMenu();

  const { config } = useApplicationContext();

  const isMenuEnabled = config.get<boolean>('frontend-chats.deleteMessagesFeature.Enabled') || false;
  const isHorizontalGalleryOffersViewEnabled =
    config.get<boolean>('frontend-chats.horizontalGalleryOffers.Enabled') ?? false;

  const handleShowContextMenu = React.useCallback(
    (event: MouseEvent): boolean => {
      if (!messageRef.current) {
        return false;
      }

      event.preventDefault();

      const position = {
        x: event.clientX,
        y: event.clientY,
      };

      showChatMessageMenu(chatId, messageId, position);

      return true;
    },
    [showChatMessageMenu, chatId, messageId],
  );

  const handleLongTouchMenu = React.useCallback(
    () => showChatMessageMenu(chatId, messageId),
    [showChatMessageMenu, chatId, messageId],
  );

  useContextMenu({
    elementRef: messageRef,
    onShowContextMenu: handleShowContextMenu,
    onHideContextMenu: hideChatMessageMenu,
    isEnabled: isMenuEnabled,
  });
  const { onTouchStart, onTouchEnd } = useLongPress(handleLongTouchMenu, () => {}, {
    shouldPreventDefault: isMenuEnabled,
  });

  React.useEffect(() => {
    if (!messageRef.current) {
      return;
    }

    const element = messageRef.current;

    element.addEventListener('touchstart', onTouchStart, { passive: true });
    element.addEventListener('touchend', onTouchEnd);

    return () => {
      element.removeEventListener('touchstart', onTouchStart);
      element.removeEventListener('touchend', onTouchEnd);
    };
  }, [onTouchStart, onTouchEnd]);

  const isMessageLoading = status === ERequestApiStatus.Pending;
  const isMessageFailed = status === ERequestApiStatus.Failed;

  const imagesNode = useImagesNode(files, messageId, isMessageLoading, isMessageFailed);
  const { imageAsDocument, imageAsDocumentDraft } = useImageAsDocument(messageId, files?.imagesAsDocuments?.[0]);
  const { document, documentDraft } = useDocument(messageId, files?.documents?.[0]);

  const { userName, nameReplacementImageUrl } = useUserName({
    messageUserId: userId,
    isFirstUserPhrase,
    withImage: Boolean(imagesNode || imageAsDocument || imageAsDocumentDraft),
    withOffer: isOfferMessage,
  });

  const handleSetShowErrorMenu = React.useCallback(() => {
    setShowErrorMenu(!showErrorMenu);
  }, [showErrorMenu]);

  const onShow = React.useCallback(() => {
    const attributes =
      isChatbotOfferMessage && isOfferMessage && isHorizontalGalleryOffersViewEnabled
        ? 'Horizontal_scroll '
        : undefined;
    trackShowMessage(chatId, messageId, communicationId, attributes, message?.offers);
  }, [chatId, offer]);

  const onClickMessage = React.useCallback(
    (e: any) => {
      if (e.target.tagName === 'A') {
        const messageType = e.target.href.includes('tel') ? 'phone' : 'link';
        const clickText = e.target.dataset.clickText;

        trackClickMessage({
          chatId,
          messageId,
          messageType,
          offerId: offer?.realtyOfferId,
          communicationId,
          clickText,
        });
      }
    },
    [chatId, offer],
  );

  const hiddenMessagePhonesClick = React.useCallback(() => {
    trackHiddenMessagePhoneClick({
      chatId,
      messageId,
      offerId: offer?.realtyOfferId,
      communicationId,
    });
  }, [chatId, communicationId, messageId, offer?.realtyOfferId]);

  const isOutcomingMessageStyle = useOutcomingMessageStyle(isOutcoming, userId);
  const formattedText = useMessageText({
    text,
    isOutcoming: isOutcomingMessageStyle,
    hiddenMessagePhonesClick,
    userId,
  });
  const formattedHtmlText = useMessageHtml(htmlText);
  const hasText = formattedText || formattedHtmlText;
  const isOnlyImage = Boolean(!hasText && imagesNode);
  const isTextAndImage = Boolean(hasText && imagesNode);
  const isUnread = !isOutcoming && !isRead;
  const showDailyrentButtons = shouldShowDailyrentButtons(actionButtons);

  if (message.type === 'textAndImages') {
    return (
      <ChatMessageChatbotGreeting
        key={messageId}
        created={message.created}
        isLastInChain={isLastInChain}
        messageUserId={message.userId}
        textNode={formattedText}
        imagesNode={message.images?.[0] && <ImageNode src={message.images[0]} />}
      />
    );
  }

  let textNode = null;

  if (message.type !== 'offer') {
    // eslint-disable-next-line react/no-danger
    textNode = formattedHtmlText ? <div dangerouslySetInnerHTML={{ __html: formattedHtmlText }} /> : formattedText;
  }

  const actionButtonsNode = actionButtonsV3?.length
    ? actionButtonsV3.map((actionButton, index) => (
        <ActionButtonContainer
          key={index}
          actionButton={actionButton}
          messageId={messageId}
          communicationId={communicationId}
          index={index}
          isOutcoming={isOutcoming}
        />
      ))
    : actionButtons?.length
      ? actionButtons.map((actionButton, index) => (
          <ActionButtonContainer
            key={index}
            actionButton={actionButton}
            messageId={messageId}
            communicationId={communicationId}
          />
        ))
      : null;

  const statusNode = isOutcoming && !isMessageFailed ? getStatusIcon(status) : null;
  const errorSignNode = isMessageFailed ? (
    <IconButton
      ref={buttonRef}
      data-name="ErrorSignNode"
      icon={<IconFunctionalWarningCircle16 />}
      onClick={handleSetShowErrorMenu}
    />
  ) : null;
  const errorTextNode = isMessageFailed ? <UIText3 color="text-negative-default">не отправлено</UIText3> : null;
  const errorMenu = showErrorMenu ? (
    <MessageErrorMenu
      buttonRef={buttonRef}
      isOfferMessage={isOfferMessage}
      messageId={messageId}
      closeMenu={handleSetShowErrorMenu}
    />
  ) : null;
  const infoMessageNode = <InfoMessageContainer isOutcoming={isOutcoming} assumptions={assumptions} />;
  const documentNode =
    document || documentDraft ? (
      <DocumentMessageContainer
        isOutcoming={isOutcoming}
        document={document}
        documentDraft={documentDraft}
        isMessageLoading={isMessageLoading}
        isMessageFailed={isMessageFailed}
        messageId={messageId}
      />
    ) : null;
  const imageAsDocumentNode =
    imageAsDocument || imageAsDocumentDraft ? (
      <ImageAsDocumentMessageContainer
        isOutcoming={isOutcoming}
        imageAsDocument={imageAsDocument}
        imageAsDocumentDraft={imageAsDocumentDraft}
        isMessageLoading={isMessageLoading}
        isMessageFailed={isMessageFailed}
        messageId={messageId}
      />
    ) : null;

  const offerNode = isOfferGallery ? (
    <OfferGalleryContainer
      isMessageFailed={isMessageFailed}
      messageId={messageId}
      message={message}
      isOutcoming={isOutcoming}
      formattedText={formattedText}
    />
  ) : (
    <OfferMessageContainer
      isMessageFailed={isMessageFailed}
      messageId={messageId}
      message={message}
      isOutcoming={isOutcoming}
      textNode={formattedText}
    />
  );

  const bubblesNode = bubbles?.length
    ? bubbles.map(bubble => <BubbleMessageContainer key={bubble.title.label} bubble={bubble} messageId={messageId} />)
    : null;

  // FIXME рефакторинг https://jira.cian.tech/browse/ACTI-100 разбить на разные типы баблов, вынести общую обертку со стилями, хвостиками
  return (
    <ChatMessageLayout
      ref={messageRef}
      actionButtonsNode={actionButtonsNode}
      bubblesNode={bubblesNode}
      documentNode={documentNode}
      errorMenuNode={errorMenu}
      errorSignNode={errorSignNode}
      errorTextNode={errorTextNode}
      imageAsDocumentNode={imageAsDocumentNode}
      imagesNode={imagesNode}
      infoMessageNode={infoMessageNode}
      offerNode={offerNode}
      showDailyrentButtons={showDailyrentButtons}
      statusNode={statusNode}
      textNode={textNode}
      created={created}
      isLastInChain={isLastInChain}
      isOffer={isOfferMessage}
      isOnlyImage={isOnlyImage}
      isOutcoming={isOutcomingMessageStyle}
      isTextAndImage={isTextAndImage}
      isUnread={isUnread}
      isChatbotOfferMessage={isChatbotOfferMessage}
      isOfferGallery={isOfferGallery}
      messageUserId={userId}
      nameReplacementImageUrl={nameReplacementImageUrl}
      userName={userName}
      onClick={onClickMessage}
      onShow={onShow}
    />
  );
};
